import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  Grid,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { AccountBalanceWalletRounded as CashBookIcon } from "@mui/icons-material";
import axios from "axios";
import { AutoTaskAuthContext } from "../../../Context/AutoTaskContext";
import { useAccountsNav } from "../../../utils/moduleNav";
import { usePawnLedgerScope } from "../pawnLedgerScope";
import { reportPageSx, reportTableSx } from "../../PawnShop/reportLayout";
import ReportDateWindow from "../../PawnShop/reportDateWindow";
import { DEFAULT_SORT, sortRows } from "../../PawnShop/reportSort";
import {
  BLUE_DARK,
  BLUE_PALE,
  EmptyState,
  FilterActions,
  FilterCard,
  HEAD_BG,
  INK,
  INK_MUTED,
  LoadingPanel,
  ResultsHeader,
  SUCCESS,
  TODAY,
  cellSx,
  fmtAmt,
  fmtAmtAlways,
  fmtBalance,
  fmtDate,
  footCellSx,
  monthStart,
  headCellSx,
  pdfTableTheme,
  rangeLabel,
  resultsCardSx,
  startPdf,
} from "./ledgerReportKit";

const API = process.env.REACT_APP_API_BASE_URL;

// "All" is its own endpoint (every account's postings in one register), so it
// lives in the picker rather than as a separate control.
//
// It means every account BELOW it in this dropdown. The endpoint's own default
// is only the accounts flagged `is_bank_account`, which no pawn store sets, so
// the request carries `include_all_accounts` to widen it — see
// getCashBankBookAll.
const ALL_ACCOUNTS = {
  account_id: "all",
  account_name: "All Accounts",
  account_code: "",
};

// What each sort option reads off a posting.
const SORT_ACCESSORS = {
  date: (r) => r.date,
  voucher: (r) => r.voucher_number,
  receipt: (r) => parseFloat(r.debit_amount) || 0,
  payment: (r) => parseFloat(r.credit_amount) || 0,
};

const SORT_OPTIONS = [
  { key: "date", label: "Date" },
  { key: "voucher", label: "Voucher No" },
  { key: "receipt", label: "Receipt" },
  { key: "payment", label: "Payment" },
];

/**
 * The cash / bank book: money in and out of one account (or of every bank and
 * cash account at once), with the contra side of each voucher spelled out.
 *
 * Unlike the ledger and trial balance this register is NOT financial-year
 * scoped on the server — it reads purely on the date window — so the FY only
 * decides where the window opens.
 */
const PawnCashBankBook = ({ embedded = false }) => {
  const { userInfo, currentStoreInfo } = useContext(AutoTaskAuthContext) || {};
  const storeId = userInfo?.store_id;
  const { go: goAccounts } = useAccountsNav();
  const { pawnMode, scope: pawnScope } = usePawnLedgerScope();

  const [accounts, setAccounts] = useState([ALL_ACCOUNTS]);
  const [account, setAccount] = useState(ALL_ACCOUNTS);

  const [dateMode, setDateMode] = useState("range");
  const [singleDate, setSingleDate] = useState(TODAY());
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(TODAY());

  const [sort, setSort] = useState(DEFAULT_SORT);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const showError = useCallback(
    (message) => setSnackbar({ open: true, message }),
    [],
  );

  const isAll = account?.account_id === "all";
  const effFrom = dateMode === "single" ? singleDate : fromDate;
  const effTo = dateMode === "single" ? singleDate : toDate;

  // ── Accounts for the picker ───────────────────────────────────────────────
  useEffect(() => {
    if (storeId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/accounts/accounts/${storeId}/accounts-only`,
        );
        const list = Array.isArray(data?.data) ? data.data : [];
        if (!cancelled) setAccounts([ALL_ACCOUNTS, ...list]);
      } catch {
        if (!cancelled) showError("Could not load the account list.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId, showError]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const buildReport = useCallback(async () => {
    if (storeId == null || !account) return;
    setLoading(true);
    try {
      const payload = {
        store_id: parseInt(storeId, 10),
        from_date: effFrom,
        to_date: effTo,
        ...pawnScope,
      };
      const { data } = await axios.post(
        isAll
          ? `${API}/api/accounts/cash-bank-book/all`
          : `${API}/api/accounts/cash-bank-book/account`,
        isAll
          ? { ...payload, include_all_accounts: true }
          : { ...payload, account_id: account.account_id },
      );
      if (data?.success) setReport(data.data);
      else {
        setReport(null);
        showError(data?.message || "Failed to generate the cash/bank book.");
      }
    } catch (err) {
      setReport(null);
      showError(
        err?.response?.data?.message ||
          "Failed to generate the cash/bank book.",
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, account, isAll, effFrom, effTo, pawnMode, showError]);

  useEffect(() => {
    buildReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, pawnMode, account, dateMode, singleDate, fromDate, toDate]);

  const handleReset = () => {
    setAccount(ALL_ACCOUNTS);
    setDateMode("range");
    setSingleDate(TODAY());
    setFromDate(monthStart());
    setToDate(TODAY());
    setSort(DEFAULT_SORT);
  };

  // ── Rows ──────────────────────────────────────────────────────────────────
  // The running balance is only meaningful in the order the book was built, so
  // the column (and the opening/closing rows that frame it) goes away the
  // moment a sort reorders things.
  const showBalance = !sort.key;

  /**
   * One flat row list drives both the table and the PDF, so the two can never
   * drift. A row is { kind, ... } where kind is:
   *
   *   "account"  a group banner — the account's name and its own totals
   *   "balance"  that account's opening / closing balance
   *   "entry"    one posting
   *
   * Reading EVERY account, the server hands back one date-ordered stream of
   * postings with a running balance that walks across account boundaries. That
   * is unreadable — and the balance is meaningless, because it is adding up
   * cash, pledge and interest as if they were one account. So the consolidated
   * view is grouped: each account gets its own block, its own opening and
   * closing balance, and its own running balance down the middle. A single
   * account is already one block, so it is left as a plain list.
   */
  const rows = useMemo(() => {
    const entries = report?.entries || [];
    if (!entries.length) return [];

    if (!isAll)
      return sortRows(entries, sort, SORT_ACCESSORS).map((e) => ({
        kind: "entry",
        ...e,
      }));

    // Dr positive / Cr negative, so a balance is one signed number to carry.
    const signed = (v, type) =>
      (parseFloat(v) || 0) * (type === "CREDIT" ? -1 : 1);
    const present = (n) => ({
      value: Math.abs(n),
      type: n < 0 ? "CREDIT" : "DEBIT",
    });

    const byAccount = new Map();
    entries.forEach((e) => {
      const key = e.account_id ?? e.bank_account_name;
      if (!byAccount.has(key)) byAccount.set(key, []);
      byAccount.get(key).push(e);
    });

    // The server orders bank_accounts by name; follow it so the blocks are in a
    // stable, alphabetical order rather than in first-voucher order. Accounts
    // with no movement in the window are simply not blocks.
    const ordered = (report.bank_accounts || []).filter((a) =>
      byAccount.has(a.account_id),
    );
    // Belt and braces: an entry whose account somehow isn't in the list still
    // gets a block rather than vanishing.
    const listed = new Set(ordered.map((a) => a.account_id));
    const orphans = [...byAccount.keys()].filter((k) => !listed.has(k));

    const out = [];
    const emit = (acct, list) => {
      const sorted = sortRows(list, sort, SORT_ACCESSORS);
      const receipts = list.reduce(
        (sum, e) => sum + (parseFloat(e.debit_amount) || 0),
        0,
      );
      const payments = list.reduce(
        (sum, e) => sum + (parseFloat(e.credit_amount) || 0),
        0,
      );
      const opening = signed(acct?.opening_balance, acct?.opening_balance_type);
      const closing = present(opening + receipts - payments);

      out.push({
        kind: "account",
        label:
          acct?.account_name || list[0]?.bank_account_name || "Unknown account",
        code: acct?.account_code || list[0]?.bank_account_code || "",
        count: list.length,
        receipts,
        payments,
        closing: closing.value,
        closingType: closing.type,
      });

      if (showBalance) {
        const o = present(opening);
        out.push({
          kind: "balance",
          label: "Opening Balance",
          balance: o.value,
          balanceType: o.type,
        });
      }

      let running = opening;
      sorted.forEach((e) => {
        running +=
          (parseFloat(e.debit_amount) || 0) - (parseFloat(e.credit_amount) || 0);
        const b = present(running);
        out.push({
          kind: "entry",
          ...e,
          // Recomputed per account: the server's running_balance walks the
          // whole consolidated stream and means nothing inside a block.
          running_balance: b.value,
          balance_type: b.type,
        });
      });

      if (showBalance)
        out.push({
          kind: "balance",
          label: "Closing Balance",
          balance: closing.value,
          balanceType: closing.type,
          closing: true,
        });
    };

    ordered.forEach((acct) => emit(acct, byAccount.get(acct.account_id)));
    orphans.forEach((key) => emit(null, byAccount.get(key)));
    return out;
  }, [report, sort, isAll, showBalance]);

  const entryRows = useMemo(
    () => rows.filter((r) => r.kind === "entry"),
    [rows],
  );
  const accountBlocks = useMemo(
    () => rows.filter((r) => r.kind === "account").length,
    [rows],
  );

  const openVoucher = (voucherNo) => {
    if (!voucherNo) return;
    // See PawnIndividualLedger — Back returns to the chip it left from.
    goAccounts("/accounts-entry", {
      state: { voucher_no: voucherNo, fromLedger: true, ledgerType: "cashbook" },
    });
  };

  // ── PDF ───────────────────────────────────────────────────────────────────
  // Prints the same rows the table shows, account blocks and all, so the paper
  // copy and the screen can never disagree.
  const generatePDF = () => {
    if (!report) return;
    const { doc, margin, startY } = startPdf({
      store: currentStoreInfo,
      title: `Cash / Bank Book — ${
        isAll
          ? "All Accounts"
          : report.account_details?.account_name || account?.account_name || ""
      }`,
      context: rangeLabel(effFrom, effTo),
    });

    const bold = { fontStyle: "bold" };
    const right = { halign: "right" };

    const body = rows.map((r) => {
      if (r.kind === "account")
        return [
          {
            content: r.code ? `${r.label} — ${r.code}` : r.label,
            colSpan: 3,
            styles: bold,
          },
          { content: fmtAmt(r.receipts), styles: { ...bold, ...right } },
          { content: fmtAmt(r.payments), styles: { ...bold, ...right } },
          {
            content: fmtBalance(r.closing, r.closingType),
            styles: { ...bold, ...right },
          },
        ];

      if (r.kind === "balance")
        return [
          { content: `    ${r.label}`, colSpan: 5, styles: bold },
          {
            content: fmtBalance(r.balance, r.balanceType),
            styles: { ...bold, ...right },
          },
        ];

      return [
        fmtDate(r.date),
        r.voucher_number || "—",
        r.contra_account_name || r.narration || "",
        { content: fmtAmt(r.debit_amount), styles: right },
        { content: fmtAmt(r.credit_amount), styles: right },
        {
          content: showBalance
            ? fmtBalance(r.running_balance, r.balance_type)
            : "",
          styles: right,
        },
      ];
    });

    // A single account has no blocks, so it keeps its opening balance line.
    if (!isAll && showBalance)
      body.unshift([
        { content: "Opening Balance", colSpan: 5, styles: bold },
        {
          content: fmtBalance(
            report.opening_balance,
            report.opening_balance_type,
          ),
          styles: { ...bold, ...right },
        },
      ]);

    doc.autoTable({
      startY,
      head: [
        ["Date", "Voucher No", "Particulars", "Receipt", "Payment", "Balance"],
      ],
      body,
      foot: [
        [
          { content: "Totals", colSpan: 3, styles: { halign: "left" } },
          { content: fmtAmtAlways(report.total_debit), styles: right },
          { content: fmtAmtAlways(report.total_credit), styles: right },
          {
            content: isAll
              ? ""
              : fmtBalance(
                  report.closing_balance,
                  report.closing_balance_type,
                ),
            styles: right,
          },
        ],
      ],
      footStyles: {
        fillColor: [248, 250, 252],
        textColor: [15, 23, 42],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 26 },
        3: { halign: "right", cellWidth: 32 },
        4: { halign: "right", cellWidth: 32 },
        5: { halign: "right", cellWidth: 38 },
      },
      ...pdfTableTheme(margin),
    });

    doc.save(`Cash_Bank_Book_${effFrom}_to_${effTo}.pdf`);
  };

  // Date, Voucher No, Particulars — the descriptive half of a row. Both views
  // now have the same three: reading every account, the account name sits on
  // the block heading instead of repeating down a column of its own.
  const LABEL_COLS = 3;

  return (
    <Box sx={reportPageSx(embedded)}>
      <FilterCard>
        <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={accounts}
              value={account}
              onChange={(_e, v) => setAccount(v || ALL_ACCOUNTS)}
              getOptionLabel={(o) =>
                o?.account_name
                  ? `${o.account_name}${o.account_code ? ` — ${o.account_code}` : ""}`
                  : ""
              }
              isOptionEqualToValue={(o, v) =>
                String(o.account_id) === String(v?.account_id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="A/C"
                  placeholder="All Accounts"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <ReportDateWindow
            label="Period"
            dateMode={dateMode}
            setDateMode={setDateMode}
            singleDate={singleDate}
            setSingleDate={setSingleDate}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
          />

          <Grid item xs={12} md="auto">
            <FilterActions
              onView={buildReport}
              onReset={handleReset}
              loading={loading}
              sortOptions={SORT_OPTIONS}
              sort={sort}
              onSortChange={setSort}
            />
          </Grid>
        </Grid>
      </FilterCard>

      <Card elevation={0} sx={resultsCardSx}>
        <ResultsHeader
          title={`CASH / BANK BOOK — ${rangeLabel(effFrom, effTo)}`}
          subtitle={[
            report?.account_details?.account_name ||
              account?.account_name ||
              "All Accounts",
            // Only one account has a single opening balance worth quoting.
            report && !isAll
              ? `Opening ${fmtBalance(
                  report.opening_balance,
                  report.opening_balance_type,
                )}`
              : null,
          ]
            .filter(Boolean)
            .join("  ·  ")}
          count={
            report
              ? `${entryRows.length} ${
                  entryRows.length === 1 ? "entry" : "entries"
                }${isAll ? ` · ${accountBlocks} a/c` : ""}`
              : null
          }
          onPdf={generatePDF}
          pdfDisabled={loading || !report}
        />

        {loading ? (
          <LoadingPanel />
        ) : !report || rows.length === 0 ? (
          <EmptyState icon={<CashBookIcon />}>
            No cash or bank movement for {rangeLabel(effFrom, effTo)}.
          </EmptyState>
        ) : (
          <TableContainer sx={reportTableSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 112 }}>Date</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 110 }}>
                    Voucher No
                  </TableCell>
                  <TableCell sx={headCellSx}>Particulars</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 130 }}>
                    Receipt
                  </TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 130 }}>
                    Payment
                  </TableCell>
                  {showBalance && (
                    <TableCell align="right" sx={{ ...headCellSx, width: 150 }}>
                      Balance
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* One account: no blocks, so the register keeps its own
                    opening balance line at the top. */}
                {!isAll && (
                  <TableRow>
                    <TableCell
                      colSpan={LABEL_COLS + 2}
                      sx={{
                        ...cellSx,
                        fontWeight: 700,
                        color: INK,
                        bgcolor: HEAD_BG,
                      }}
                    >
                      Opening Balance
                    </TableCell>
                    {showBalance && (
                      <TableCell
                        align="right"
                        sx={{
                          ...cellSx,
                          fontWeight: 700,
                          color: INK,
                          bgcolor: HEAD_BG,
                        }}
                      >
                        {fmtBalance(
                          report.opening_balance,
                          report.opening_balance_type,
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )}

                {rows.map((r, i) => {
                  // An account's block heading: its name, and its own totals
                  // for the window so the block can be read on its own.
                  if (r.kind === "account")
                    return (
                      <TableRow key={`g-${i}`}>
                        <TableCell
                          colSpan={LABEL_COLS}
                          sx={{
                            ...cellSx,
                            fontWeight: 800,
                            color: INK,
                            bgcolor: HEAD_BG,
                          }}
                        >
                          {r.label}
                          {r.code ? (
                            <Box
                              component="span"
                              sx={{ color: INK_MUTED, fontWeight: 600 }}
                            >
                              {` — ${r.code}`}
                            </Box>
                          ) : null}
                          <Box
                            component="span"
                            sx={{
                              color: INK_MUTED,
                              fontWeight: 600,
                              fontSize: "0.72rem",
                            }}
                          >
                            {`  ·  ${r.count} ${r.count === 1 ? "entry" : "entries"}`}
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ ...cellSx, bgcolor: HEAD_BG, fontWeight: 700 }}
                        >
                          {fmtAmt(r.receipts)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ ...cellSx, bgcolor: HEAD_BG, fontWeight: 700 }}
                        >
                          {fmtAmt(r.payments)}
                        </TableCell>
                        {showBalance && (
                          <TableCell
                            align="right"
                            sx={{
                              ...cellSx,
                              bgcolor: HEAD_BG,
                              fontWeight: 800,
                              color: BLUE_DARK,
                            }}
                          >
                            {fmtBalance(r.closing, r.closingType)}
                          </TableCell>
                        )}
                      </TableRow>
                    );

                  // That account's opening / closing balance.
                  if (r.kind === "balance")
                    return (
                      <TableRow
                        key={`b-${i}`}
                        sx={r.closing ? { bgcolor: BLUE_PALE } : undefined}
                      >
                        <TableCell
                          colSpan={LABEL_COLS + 2}
                          sx={{
                            ...cellSx,
                            pl: 4,
                            color: INK_MUTED,
                            fontWeight: 600,
                          }}
                        >
                          {r.label}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ ...cellSx, fontWeight: 700, color: INK }}
                        >
                          {fmtBalance(r.balance, r.balanceType)}
                        </TableCell>
                      </TableRow>
                    );

                  return (
                    <Tooltip
                      key={`e-${i}`}
                      title="Double-click to open this voucher"
                      followCursor
                    >
                      <TableRow
                        hover
                        onDoubleClick={() => openVoucher(r.voucher_number)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell
                          sx={{
                            ...cellSx,
                            whiteSpace: "nowrap",
                            pl: isAll ? 4 : undefined,
                          }}
                        >
                          {fmtDate(r.date)}
                        </TableCell>
                        <TableCell
                          sx={{ ...cellSx, fontWeight: 700, color: INK }}
                        >
                          {r.voucher_number || "—"}
                        </TableCell>
                        <TableCell sx={cellSx}>
                          {r.contra_account_name || r.narration || ""}
                        </TableCell>
                        <TableCell align="right" sx={cellSx}>
                          {fmtAmt(r.debit_amount)}
                        </TableCell>
                        <TableCell align="right" sx={cellSx}>
                          {fmtAmt(r.credit_amount)}
                        </TableCell>
                        {showBalance && (
                          <TableCell
                            align="right"
                            sx={{ ...cellSx, color: INK, fontWeight: 600 }}
                          >
                            {fmtBalance(r.running_balance, r.balance_type)}
                          </TableCell>
                        )}
                      </TableRow>
                    </Tooltip>
                  );
                })}

                {!isAll && (
                  <TableRow sx={{ bgcolor: BLUE_PALE }}>
                    <TableCell
                      colSpan={LABEL_COLS + 2}
                      sx={{
                        ...cellSx,
                        fontWeight: 700,
                        color: INK,
                        borderBottom: "none",
                      }}
                    >
                      Closing Balance
                    </TableCell>
                    {showBalance && (
                      <TableCell
                        align="right"
                        sx={{
                          ...cellSx,
                          fontWeight: 800,
                          color: INK,
                          borderBottom: "none",
                        }}
                      >
                        {fmtBalance(
                          report.closing_balance,
                          report.closing_balance_type,
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TableCell colSpan={LABEL_COLS} sx={footCellSx}>
                    Totals
                  </TableCell>
                  <TableCell align="right" sx={{ ...footCellSx, color: SUCCESS }}>
                    {fmtAmtAlways(report.total_debit)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...footCellSx, color: SUCCESS }}>
                    {fmtAmtAlways(report.total_credit)}
                  </TableCell>
                  {showBalance && (
                    <TableCell align="right" sx={footCellSx}>
                      {/* Across every account one closing balance would be cash
                          + pledge + interest added together, which is not a
                          balance of anything — each block carries its own. */}
                      {isAll
                        ? ""
                        : fmtBalance(
                            report.closing_balance,
                            report.closing_balance_type,
                          )}
                    </TableCell>
                  )}
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PawnCashBankBook;
